import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';
import { warnIfTruncated } from '@/lib/warn-if-truncated';

// Shared data layer for autocomplete wrappers. Each wrapper supplies tiny
// closures over its domain API + queryKey factory; this hook handles the
// load strategy, debounce, open/query state, and the selected-detail fetch.
//
//   - 'preload': fetch one full page (up to `preloadLimit`) the first time
//     the popover opens and cache it for 5 min. The Autocomplete filters in
//     memory. No network on keystrokes. If the catalog outgrows the page,
//     `truncated` is set so the UI can show a "first N of M" footer.
//   - 'lazy': fetch only when the popover opens, with a debounced `q`.
//     Cached for 60 s. One `lazyLimit` page at a time.
//   - 'always': like lazy but `staleTime: 0` — every open refetches.
//
// `excludeCount` (typically `excludeIds?.length ?? 0`) is folded into the
// lazy/always limit so that worst-case client-side exclusion filtering
// still surfaces a useful page. Without this, a Skill picker with 15
// already-assigned skills sees the server's 20-item page collapse to 5
// visible items. Preload pulls the full list once, so excludeCount has no
// bearing there — and pushing past the server's max-limit gate would 400.
export type LoadStrategy = 'preload' | 'lazy' | 'always';

interface ListResult<T> {
  data: T[];
  /** Pagination metadata, when the underlying endpoint returns it. Used only
   *  to detect a truncated preload (see the dev warning below). */
  meta?: { total: number };
}

interface DetailResult<T> {
  data: T;
}

interface Options<T> {
  value: string | null;
  strategy: LoadStrategy;
  fetchList: (opts: { q?: string; limit: number }) => Promise<ListResult<T>>;
  fetchOne: (id: string) => Promise<DetailResult<T>>;
  listKey: (opts: { q?: string; limit: number }) => readonly unknown[];
  detailKey: (id: string) => readonly unknown[];
  excludeCount?: number;
  preloadLimit?: number;
  lazyLimit?: number;
  debounceMs?: number;
  /** Preload only: fetch the catalog on mount (not just on first open) so a
   *  picker seeded with selected ids can resolve their labels from the list
   *  without the user opening it. Set by multi-pickers that render selected
   *  badges. No effect in lazy/always mode. */
  resolveSelected?: boolean;
}

export interface EntityAutocomplete<T> {
  items: readonly T[];
  selectedItem: T | null;
  loading: boolean;
  isClient: boolean;
  /** Non-null when the preloaded page is smaller than the server's total —
   *  the catalog outgrew `preloadLimit`. Render a footer telling the user
   *  only the first `shown` of `total` entries are listed. Always null in
   *  lazy/always mode. */
  truncated: { shown: number; total: number } | null;
  setQuery: (q: string) => void;
  setOpen: (o: boolean) => void;
}

export function useEntityAutocomplete<T>({
  value,
  strategy,
  fetchList,
  fetchOne,
  listKey,
  detailKey,
  excludeCount = 0,
  preloadLimit = 200,
  lazyLimit = 20,
  debounceMs = 200,
  resolveSelected = false,
}: Options<T>): EntityAutocomplete<T> {
  const isPreload = strategy === 'preload';
  const [open, setOpen] = useState(false);
  // Sticky "popover has been opened at least once" gate for the preload
  // fetch. Preload pulls a large page (up to `preloadLimit` rows); paying
  // that on MOUNT means a page rendering N pickers fetches N catalogs the
  // user may never look at (the timesheet day list mounts one picker per
  // entry card). Gating on first open keeps the in-memory-filter semantics
  // while fetching only for pickers actually interacted with. Autocomplete
  // fires `onOpenChange` on mount for `defaultOpen` consumers, so those
  // still fetch immediately.
  const [everOpened, setEverOpened] = useState(false);
  const handleSetOpen = useCallback((o: boolean) => {
    setOpen(o);
    if (o) setEverOpened(true);
  }, []);
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const h = setTimeout(() => setDebounced(query), debounceMs);
    return () => clearTimeout(h);
  }, [query, debounceMs]);

  const selected = useQuery({
    queryKey: detailKey(value ?? ''),
    queryFn: () => fetchOne(value as string),
    enabled: Boolean(value),
  });

  const preloadOpts = { limit: preloadLimit };
  const preload = useQuery({
    queryKey: listKey(preloadOpts),
    queryFn: () => fetchList(preloadOpts),
    enabled: isPreload && (everOpened || resolveSelected),
    staleTime: 5 * 60_000,
  });
  // Preload fetches one capped page and filters in memory. If the catalog
  // has outgrown `preloadLimit` the page is incomplete; surface that to the
  // caller as `truncated` so the UI tells the user only the first page is
  // listed (Autocomplete footer) instead of presenting a partial catalog as
  // complete. We deliberately do NOT degrade to the lazy server-search path
  // here — that showed only `lazyLimit` rows until the user typed, which
  // reads as "entries are missing" (the bug preload exists to fix). Also
  // warn in dev. The label is the entity name (first query-key segment);
  // its value is stable across renders.
  const truncated =
    isPreload && preload.data?.meta && preload.data.data.length < preload.data.meta.total
      ? { shown: preload.data.data.length, total: preload.data.meta.total }
      : null;
  const preloadLabel = `${String(listKey(preloadOpts)[0])} autocomplete`;
  useEffect(() => {
    if (isPreload) warnIfTruncated(preload.data, preloadLabel);
  }, [isPreload, preload.data, preloadLabel]);

  const isClient = isPreload;

  const lazyOpts = { q: debounced || undefined, limit: lazyLimit + excludeCount };
  const lazy = useQuery({
    queryKey: listKey(lazyOpts),
    queryFn: () => fetchList(lazyOpts),
    placeholderData: keepPreviousData,
    enabled: !isPreload && open,
    staleTime: strategy === 'always' ? 0 : 60_000,
  });

  return {
    items: isClient ? (preload.data?.data ?? []) : (lazy.data?.data ?? []),
    selectedItem: selected.data?.data ?? null,
    loading: isClient ? preload.isLoading : lazy.isLoading,
    isClient,
    truncated,
    setQuery,
    setOpen: handleSetOpen,
  };
}
